import type { AccessRestrictionCode, User } from "@academia-hub/shared-types";

/**
 * External, mutable auth state — deliberately NOT a React Context value,
 * since `lib/api-client.ts` (plain `fetch` wrapper, outside React) needs to
 * read the current access token synchronously on every request, including
 * mid-request during a silent refresh triggered before any component has
 * had a chance to read the new value through a hook. `useAuth()` subscribes
 * to this via `useSyncExternalStore`, the same pattern `ThemeProvider` uses
 * for the OS color-scheme (`packages/ui`'s `theme-provider.tsx`) — the
 * React-endorsed way to read a genuinely external source without mirroring
 * it into `useState`.
 *
 * The access token lives ONLY here, in memory — never `localStorage`
 * (`00_CLAUDE_CODE_MASTER_PROMPT.md` / ADR-0004). A page reload always
 * starts from `status: "loading"` and re-derives the session from the
 * `HttpOnly` refresh cookie (`AuthProvider`'s bootstrap effect).
 *
 * `accessToken` is tracked separately from `user`/`roles`/`permissions`
 * (rather than nesting everything under one `session | null` field) because
 * `updateAccessToken` — called mid-flight by `api-client.ts`'s automatic
 * refresh-and-retry — must be able to set a freshly rotated token even
 * before the bootstrap flow has populated the rest of the profile (the very
 * first `/auth/me` call on page load *is* what triggers that first refresh).
 */
export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

/**
 * Set while the API refuses the account everything but a forced setup step (ADR-0026 §15):
 * `RequireAuth` then shows the "Choose a new password" or "Set up two-factor authentication"
 * screen instead of the app. `PASSWORD_CHANGE_REQUIRED` comes first when both apply.
 */
export type AuthRestriction = AccessRestrictionCode;

const RESTRICTION_CODES: ReadonlySet<string> = new Set<AuthRestriction>(["PASSWORD_CHANGE_REQUIRED", "MFA_SETUP_REQUIRED"]);

export function isAuthRestriction(code: string): code is AuthRestriction {
  return RESTRICTION_CODES.has(code);
}

/** The restriction the login response or `GET /auth/me` flags imply. */
export function restrictionFrom(flags: { mustChangePassword: boolean; mfaSetupRequired: boolean }): AuthRestriction | null {
  if (flags.mustChangePassword) return "PASSWORD_CHANGE_REQUIRED";
  if (flags.mfaSetupRequired) return "MFA_SETUP_REQUIRED";
  return null;
}

export interface AuthState {
  status: AuthStatus;
  accessToken: string | null;
  user: User | null;
  roles: string[];
  permissions: string[];
  restriction: AuthRestriction | null;
  /**
   * Unauthenticated because the user chose "Sign out" (not because a session expired): the login page then
   * starts clean — no `returnTo` to the page they left, which the next person to sign in must not inherit.
   */
  signedOut?: boolean;
  /** Who was signed in before this session ended, if anyone — a `returnTo` is only honoured for the same person. */
  previousUserId?: string | null;
}

export interface AuthenticatedSession {
  accessToken: string;
  user: User;
  roles: string[];
  permissions: string[];
  /** Omitted means none. */
  restriction?: AuthRestriction | null;
}

type Listener = () => void;

const UNAUTHENTICATED_STATE: AuthState = {
  status: "unauthenticated",
  accessToken: null,
  user: null,
  roles: [],
  permissions: [],
  restriction: null,
};

let state: AuthState = { ...UNAUTHENTICATED_STATE, status: "loading" };
const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeAuth(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getAuthSnapshot(): AuthState {
  return state;
}

export function setAuthenticated(session: AuthenticatedSession): void {
  state = { status: "authenticated", ...session, restriction: session.restriction ?? null, signedOut: false, previousUserId: null };
  emit();
}

/**
 * Called by `api-client.ts` when any request comes back `403` with a restriction code — e.g. an
 * administrator required a password change mid-session. A no-op unless signed in.
 */
export function setRestriction(restriction: AuthRestriction | null): void {
  if (state.status !== "authenticated" || state.restriction === restriction) return;
  state = { ...state, restriction };
  emit();
}

export function setUnauthenticated(options: { signedOut?: boolean } = {}): void {
  // An explicit sign-out stays one even if a request still in flight fails afterwards and lands here again.
  const signedOut = options.signedOut ?? (state.status === "unauthenticated" && state.signedOut === true);
  const previousUserId = state.user?.id ?? state.previousUserId ?? null;
  state = { ...UNAUTHENTICATED_STATE, signedOut, previousUserId };
  emit();
}

/** Synchronous read for `api-client.ts` — outside React, so it can't use the hook. */
export function getAccessToken(): string | null {
  return state.accessToken;
}

/** Swaps in a freshly rotated access token, independent of `status` — see the module doc comment. */
export function updateAccessToken(accessToken: string): void {
  state = { ...state, accessToken };
  emit();
}
